import {
  RenderPresetColor,
  RenderPresetCropPosition,
  RenderPresetCropRatio,
  RenderPresetFitMode,
  RenderPresetFont,
  RenderPresetSubtitleBackground,
  ShortsRenderPresetRequest,
} from './types/shortsRenderPresetType';

/**
 * Fonts the transcode image actually ships. The other three are selectable on the
 * backend but have no font file on the server yet — a preset using one saves fine and
 * then fails the Batch job at render time, so they stay disabled here until the
 * backend confirms the files are deployed. There is no API that reports availability.
 */
export const RENDER_PRESET_AVAILABLE_FONTS: RenderPresetFont[] = ['noto_cjk_kr'];

export const RENDER_PRESET_FONT_LABELS: Record<RenderPresetFont, string> = {
  noto_cjk_kr: 'Noto Sans CJK KR',
  nemo_round: '네모니 라운드',
  sebang_b: '세방 Bold',
  gyeokdong: '격동고딕',
};

export const RENDER_PRESET_FONT_OPTIONS: {
  value: RenderPresetFont;
  label: string;
  disabled: boolean;
}[] = (Object.keys(RENDER_PRESET_FONT_LABELS) as RenderPresetFont[]).map(font => {
  const available = RENDER_PRESET_AVAILABLE_FONTS.includes(font);
  return {
    value: font,
    label: available
      ? RENDER_PRESET_FONT_LABELS[font]
      : `${RENDER_PRESET_FONT_LABELS[font]} (준비 중)`,
    disabled: !available,
  };
});

export const RENDER_PRESET_COLOR_LABELS: Record<RenderPresetColor, string> = {
  white: 'White',
  black: 'Black',
};

export const RENDER_PRESET_COLOR_OPTIONS: { value: RenderPresetColor; label: string }[] = [
  { value: 'white', label: 'White' },
  { value: 'black', label: 'Black' },
];

export const RENDER_PRESET_SUBTITLE_BACKGROUND_LABELS: Record<
  RenderPresetSubtitleBackground,
  string
> = {
  outline: 'Outline',
  box: 'Box',
};

/** px bounds the backend enforces on `subtitle_bg_opacity`. */
export const RENDER_PRESET_OPACITY_MIN = 0;
export const RENDER_PRESET_OPACITY_MAX = 100;

export const RENDER_PRESET_FIT_MODE_LABELS: Record<RenderPresetFitMode, string> = {
  fit: 'Fit (letterbox)',
  crop: 'Crop (fill)',
};

export const RENDER_PRESET_FIT_MODE_OPTIONS: { value: RenderPresetFitMode; label: string }[] = [
  { value: 'fit', label: RENDER_PRESET_FIT_MODE_LABELS.fit },
  { value: 'crop', label: RENDER_PRESET_FIT_MODE_LABELS.crop },
];

export const RENDER_PRESET_CROP_RATIO_LABELS: Record<RenderPresetCropRatio, string> = {
  full_fill: 'Full fill (1080×1920)',
  '3:4': '3:4 (1080×1440)',
  '1:1': '1:1 (1080×1080)',
};

export const RENDER_PRESET_CROP_RATIO_OPTIONS: {
  value: RenderPresetCropRatio;
  label: string;
}[] = [
  { value: 'full_fill', label: RENDER_PRESET_CROP_RATIO_LABELS.full_fill },
  { value: '3:4', label: RENDER_PRESET_CROP_RATIO_LABELS['3:4'] },
  { value: '1:1', label: RENDER_PRESET_CROP_RATIO_LABELS['1:1'] },
];

export const RENDER_PRESET_CROP_POSITION_LABELS: Record<RenderPresetCropPosition, string> = {
  left: 'Left',
  center: 'Center',
  right: 'Right',
};

export const RENDER_PRESET_CROP_POSITION_OPTIONS: {
  value: RenderPresetCropPosition;
  label: string;
}[] = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

/**
 * Output canvas per fit/crop combination. Width is always 1080; only the height moves.
 * Used to tell the operator what resolution a preset produces.
 */
export const RENDER_PRESET_OUTPUT_SIZES: Record<RenderPresetCropRatio, string> = {
  full_fill: '1080×1920',
  '3:4': '1080×1440',
  '1:1': '1080×1080',
};

export const RENDER_PRESET_FIT_OUTPUT_SIZE = '1080×1920';

/** px bounds the backend enforces on the four `size_*` fields. */
export const RENDER_PRESET_SIZE_MIN = 24;
export const RENDER_PRESET_SIZE_MAX = 120;

/** px bounds the backend enforces on `top_margin` / `bottom_margin`. */
export const RENDER_PRESET_MARGIN_MIN = 0;
export const RENDER_PRESET_MARGIN_MAX = 600;

/**
 * Server defaults, mirrored so the create form opens on the same values the backend
 * would have filled in. Sending them explicitly also keeps the font/bg colour pair
 * consistent — see the note in the form modal.
 */
export const RENDER_PRESET_DEFAULTS: Required<
  Omit<ShortsRenderPresetRequest, 'name' | 'platform_id' | 'crop_ratio' | 'description'>
> & { crop_ratio: null; description: string } = {
  font: 'noto_cjk_kr',
  font_color: 'white',
  bg_color: 'black',
  size_episode: 64,
  size_date: 44,
  size_program: 72,
  size_subtitle: 56,
  subtitle_background: 'outline',
  subtitle_bg_opacity: 60,
  fit_mode: 'fit',
  crop_ratio: null,
  crop_position: 'center',
  show_episode_name: true,
  show_on_air_date: true,
  show_program_name: true,
  top_margin: 120,
  bottom_margin: 120,
  description: '',
};

/** Placeholder for optional fields left unset. */
export const RENDER_PRESET_EMPTY_DISPLAY = '-';

/** Backend caps page size at 100. Used by the season/convert selects, which load one page. */
export const RENDER_PRESET_MAX_PAGE_SIZE = 100;

export const RENDER_PRESET_TABLE_PAGE_SIZE = 20;

/** Backend max_length on `label_episode` / `label_program` in convert-to-shorts. */
export const SHORTS_LABEL_MAX_LENGTH = 255;

/**
 * Preview debounce. The backend runs a real ffmpeg pass per call, so firing on every
 * slider tick would queue work on the API box — wait until the value settles.
 */
export const RENDER_PREVIEW_DEBOUNCE_MS = 800;
